import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import type { AxiosInstance } from "axios";

let productPlanificationApi: AxiosInstance | null = null;
let productPlanificationApiPromise: Promise<AxiosInstance> | null = null;

const getProductPlanificationApi = async (): Promise<AxiosInstance> => {
  if (productPlanificationApi) return productPlanificationApi;

  productPlanificationApiPromise ??= createAxiosWithAuth(BASE_URL, "jwt").then(
    (api) => {
      productPlanificationApi = api;
      return api;
    },
  );

  return productPlanificationApiPromise;
};

export interface PlanificacionProductivaMotivoDesvioBody {
  idMotivo: number;
  valorHectareas: number;
}

export interface PlanificacionProductivaCultivoBody {
  idGrano: number;
  hectareasAsembrar: number;
  fechaEstimadaSiembra?: string;
  consumoPropio: number;
  consumoPropioPicado: number;
  costoEstimado: number;
  toneladasRinde: number;
}

export interface CreatePlanificacionProductivaBody {
  cuentaOrigen: number;
  idCampana: number;
  idZonaProductiva: number;
  haPropias: number;
  haAlquiladas: number;
  detalle?: string;
  idUsuarioMobile?: number;
  motivosDesvio: PlanificacionProductivaMotivoDesvioBody[];
  cultivos: PlanificacionProductivaCultivoBody[];
}

export interface PlanificacionProductivaCultivoUpdateBody {
  id: number;
  idGrano: number;
  hectareasASembrar: number;
  consumoPropio: number;
  consumoPropioPicado: number;
  costoEstimado: number;
  toneladasRinde: number;
  fechaEstimadaSiembra?: string;
}

export interface UpdatePlanificacionProductivaBody {
  id: number;
  idEstado: number;
  idZonaProductiva?: number;
  detalle?: string;
  motivosDesvio: PlanificacionProductivaMotivoDesvioBody[];
  cultivos: PlanificacionProductivaCultivoUpdateBody[];
}

export interface PlanificacionProductivaDetail {
  id: number;
  idEstado: number;
  estado?: string;
  cuentaOrigen: number;
  idUsuarioMobile?: number;
  idCamp?: number;
  idCampana?: number;
  idZonaProductiva?: number;
  campana?: string;
  hectareasPropias: number;
  hectareasAlquiladas: number;
  detalle?: string;
  motivosDesvio: PlanificacionProductivaMotivoDesvioBody[];
  fechaCreacion?: string;
  fechaActualizacion?: string;
  cultivos: Array<{
    id: number;
    idCultivo?: number;
    idGrano?: number;
    fechaEstimadaSiembra?: string;
    hectareasASembrar: number;
    consumoPropio: number;
    consumoPropioPicado: number;
    costoEstimado: number;
    toneladasRinde: number;
  }>;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  statusCode: number;
}

const unwrapApiResponse = <T,>(payload: unknown): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

export interface PlanificacionProductivaListItem {
  id: number;
  estado?: string;
  cuentaOrigen?: number;
  titularCuenta?: string;
  idCampana?: number;
  campana?: string;
  hectareasPropias?: number;
  hectareasAlquiladas?: number;
  fechaCreacion?: string;
  nombreCultivo?: string;
  desvioHectareas?: boolean;
}

export interface PlanificacionProductivaListResponse {
  data: PlanificacionProductivaListItem[];
  totalPages: number;
  totalRecords: number;
}

export interface GetPlanificacionProductivaListProps {
  "P.Pagina"?: number;
  "P.Records"?: number;
  CuentaOrigen?: number;
  Cultivos?: number[];
  Estados?: number[];
  ConDesvioHectareas?: boolean;
  HectareasPropias?: number;
  HectareasAlquiladas?: number;
}

export const getPlanificacionProductivaList = async (
  props: GetPlanificacionProductivaListProps = {},
): Promise<PlanificacionProductivaListResponse | null> => {
  const api = await getProductPlanificationApi();

  const qs = objectToQueryStringWithArray(props);
  const url = qs ? `/PlanificacionProductiva?${qs}` : `/PlanificacionProductiva`;

  const response = await api.get(url);
  const unwrapped = unwrapApiResponse<unknown>(response.data);

  if (unwrapped && typeof unwrapped === "object") {
    const obj = unwrapped as any;
    if (Array.isArray(obj?.data)) {
      return obj as PlanificacionProductivaListResponse;
    }
    if (Array.isArray(obj?.listado)) {
      return {
        data: obj.listado,
        totalPages: obj.totalPages,
        totalRecords: obj.totalRecords,
      };
    }
  }

  return unwrapped as PlanificacionProductivaListResponse;
};

export const createPlanificacionProductiva = async (
  body: CreatePlanificacionProductivaBody,
) => {
  const api = await getProductPlanificationApi();

  const response = await api.post(`/PlanificacionProductiva`, body);
  return response.data;
};

export const getPlanificacionProductivaById = async (
  id: number,
): Promise<PlanificacionProductivaDetail | null> => {
  const api = await getProductPlanificationApi();
  const response = await api.get(`/PlanificacionProductiva/${id}`);
  const unwrapped = unwrapApiResponse<unknown>(response.data);
  return unwrapped as PlanificacionProductivaDetail;
};

export const updatePlanificacionProductiva = async (
  id: number,
  body: UpdatePlanificacionProductivaBody,
) => {
  const api = await getProductPlanificationApi();
  const response = await api.patch(`/PlanificacionProductiva/${id}`, body);
  return response.data;
};
